import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FacilityUseFormData,
  fetchPrefill,
  submitForm,
  downloadPdfFromBase64,
} from '../services/facilityUseService';
import './FacilityUsePage.css';

const EMPTY_FORM: FacilityUseFormData = {
  group_type: null,
  group_type_other: '',
  group_name: '',
  form_filled_date: '',
  activity: '',
  responsible_party_name: '',
  telephone: '',
  email: '',
  date_of_use: '',
  start_time: '',
  end_time: '',
  num_people_expected: '',
  security_open: false,
  security_close: false,
  security_waived: false,
  insurance_required: false,
  spaces: [
    { name: 'Church', fee: '' },
    { name: '', fee: '' },
    { name: '', fee: '' },
    { name: '', fee: '' },
  ],
  security_open_fee: '',
  security_close_fee: '',
  total_fee: '',
  extras: [
    { label: '', amount: '' },
    { label: '', amount: '' },
    { label: '', amount: '' },
    { label: '', amount: '' },
  ],
  signature_name: '',
  signature_date: '',
};

const FacilityUsePage: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const [form, setForm] = useState<FacilityUseFormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFees, setShowFees] = useState(false);
  const [success, setSuccess] = useState<{
    emailAlreadyKnown: boolean;
    personalLink: string | null;
    pdfBase64: string;
    filename: string;
  } | null>(null);

  // Default form_filled_date to today.
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    setForm(f => ({ ...f, form_filled_date: f.form_filled_date || today, signature_date: f.signature_date || today }));
  }, []);

  // If token in URL, fetch prefill.
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchPrefill(token)
      .then(data => {
        const last = data.lastFormData || {};
        setForm(f => ({
          ...f,
          email: data.email,
          group_type: (data.identity.group_type as any) ?? f.group_type,
          group_type_other: data.identity.group_type_other ?? '',
          group_name: data.identity.group_name ?? '',
          activity: data.identity.activity ?? '',
          responsible_party_name: data.identity.responsible_party_name ?? '',
          telephone: data.identity.telephone ?? '',
          // Pull last-submission values too — repeat users usually book similar
          // events and prefer to tweak rather than retype. Dates are reset to
          // today so they don't accidentally re-submit a past date.
          date_of_use: last.date_of_use ?? f.date_of_use,
          start_time: last.start_time ?? f.start_time,
          end_time: last.end_time ?? f.end_time,
          num_people_expected: last.num_people_expected ?? f.num_people_expected,
          security_open: !!last.security_open,
          security_close: !!last.security_close,
          security_waived: !!last.security_waived,
          insurance_required: !!last.insurance_required,
          spaces: last.spaces ?? f.spaces,
          extras: last.extras ?? f.extras,
          signature_name: last.signature_name ?? f.signature_name,
        }));
      })
      .catch(err => {
        setError(err?.response?.data?.error || 'Could not load your saved info');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const update = (k: keyof FacilityUseFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const v = (e.target as any).type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  const updateSpace = (i: number, field: 'name' | 'fee') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => {
      const spaces = [...f.spaces];
      spaces[i] = { ...spaces[i], [field]: e.target.value };
      return { ...f, spaces };
    });
  };
  const updateExtra = (i: number, field: 'label' | 'amount') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => {
      const extras = [...f.extras];
      extras[i] = { ...extras[i], [field]: e.target.value };
      return { ...f, extras };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.email) {
      setError('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitForm({ token: token || null, form });
      const personalLink = result.token
        ? `${window.location.origin}/facility-use/r/${result.token}`
        : null;
      setSuccess({
        emailAlreadyKnown: result.emailAlreadyKnown,
        personalLink,
        pdfBase64: result.pdfBase64,
        filename: result.filename,
      });
    } catch (err: any) {
      const data = err?.response?.data;
      if (Array.isArray(data?.errors)) {
        setError(`${data.error || 'Please complete all required fields'}: ${data.errors.join(', ')}`);
      } else {
        setError(data?.error || err?.message || 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="fu-page"><p>Loading your saved info…</p></div>;

  if (success) {
    return (
      <div className="fu-page">
        <div className="fu-success">
          <h1>Thank you — your agreement has been submitted.</h1>
          <p>We've emailed a copy of the filled agreement to <strong>{form.email}</strong>. The church office (sl2.mainoffice@gmail.com) has also been copied so they have a record.</p>

          <div className="fu-deposit-box">
            <h3>Required deposit</h3>
            <p>A <strong>$100 Security Deposit</strong> is required to hold your date. Please send your deposit to St. Luke's office to finalize the booking. Make checks payable to <em>"St. Luke's Episcopal Church"</em>.</p>
            {form.total_fee && (
              <p>Remaining facility fees (<strong>${form.total_fee}</strong> total) are due no later than 2 weeks prior to your event.</p>
            )}
          </div>

          <div className="fu-next-box">
            <h3>What happens next</h3>
            <p>The church office will send a confirmation email once they've reviewed your request. If you don't receive one within a few days, please reach out to <a href="mailto:sl2.mainoffice@gmail.com">sl2.mainoffice@gmail.com</a> to confirm.</p>
          </div>

          <p>
            <button
              className="fu-btn fu-btn-primary"
              onClick={() => downloadPdfFromBase64(success.pdfBase64, success.filename)}
            >
              Download a copy of the PDF
            </button>
          </p>

          {success.personalLink && (
            <div className="fu-link-box">
              <p><strong>Save this link for next time</strong> — opening it will pre-fill your group info so you only need to update event-specific details (date, times, attendance):</p>
              <code>{success.personalLink}</code>
              <p className="fu-hint">We've also emailed this link to {form.email}.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fu-page">
      <div className="fu-form-card">
        <header>
          <h1>One-Time Facility Use Agreement</h1>
          <p className="fu-subtitle">St. Luke's ~ San Lucas Episcopal Church · 426 E. Fourth Plain Blvd, Vancouver, WA</p>
          {token && <p className="fu-prefilled-banner">✓ Welcome back — your group info has been pre-filled.</p>}
          {!token && (
            <div className="fu-returning-banner">
              <strong>Have you used this form before?</strong> Open your saved link to pre-fill your info — we emailed it to you last time. Lost the link? Just fill out the form below and we'll send you a fresh one.
            </div>
          )}
        </header>

        {error && <div className="fu-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <fieldset>
            <legend>Group</legend>
            <label>Type of Group <span className="fu-req">*</span>
              <select required value={form.group_type || ''} onChange={update('group_type')}>
                <option value="">Select...</option>
                <option value="non-profit">Non-Profit</option>
                <option value="private-member">Private (Member)</option>
                <option value="private-non-member">Private (Non-Member)</option>
                <option value="other">Other</option>
              </select>
            </label>
            {form.group_type === 'other' && (
              <label>Other (please specify) <span className="fu-req">*</span>
                <input type="text" required value={form.group_type_other || ''} onChange={update('group_type_other')} />
              </label>
            )}
            <label>Name of Group <span className="fu-req">*</span>
              <input type="text" required value={form.group_name || ''} onChange={update('group_name')} />
            </label>
            <label>Activity <span className="fu-req">*</span>
              <input type="text" required value={form.activity || ''} onChange={update('activity')} />
            </label>
            <label>Responsible Party's Name <span className="fu-req">*</span>
              <input type="text" required value={form.responsible_party_name || ''} onChange={update('responsible_party_name')} />
            </label>
            <div className="fu-row">
              <label>Telephone <span className="fu-req">*</span>
                <input type="tel" required value={form.telephone || ''} onChange={update('telephone')} />
              </label>
              <label>Email <span className="fu-req">*</span>
                <input type="email" required value={form.email} onChange={update('email')} />
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>Event</legend>
            <div className="fu-row">
              <label>Date of Use <span className="fu-req">*</span>
                <input type="date" required value={form.date_of_use || ''} onChange={update('date_of_use')} />
              </label>
              <label>Number of People expected <span className="fu-req">*</span>
                <input type="number" required min={0} value={form.num_people_expected ?? ''} onChange={update('num_people_expected')} />
              </label>
            </div>
            <div className="fu-row">
              <label>Start Time (incl. setup) <span className="fu-req">*</span>
                <input type="time" required value={form.start_time || ''} onChange={update('start_time')} />
              </label>
              <label>End Time (vacated by) <span className="fu-req">*</span>
                <input type="time" required value={form.end_time || ''} onChange={update('end_time')} />
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>Security</legend>
            <p className="fu-hint">A security person is required to open and/or close the building outside of normal hours (M-F 9 AM – 9 PM).</p>
            <label className="fu-check"><input type="checkbox" checked={form.security_open} onChange={update('security_open')} /> Open ($25)</label>
            <label className="fu-check"><input type="checkbox" checked={form.security_close} onChange={update('security_close')} /> Close ($25)</label>
            <label className="fu-check"><input type="checkbox" checked={form.security_waived} onChange={update('security_waived')} /> Waived</label>
          </fieldset>

          <fieldset>
            <legend>Liability Insurance</legend>
            <label className="fu-check">
              <input type="checkbox" checked={form.insurance_required} onChange={update('insurance_required')} />
              Insurance Required (event open to the public)
            </label>
            <p className="fu-hint">If your event is open to the public, liability insurance must be procured and secured no less than one week prior to the event.</p>
          </fieldset>

          <fieldset>
            <legend className="fu-collapsible" onClick={() => setShowFees(s => !s)}>
              {showFees ? '▾' : '▸'} Fees (fill in if known — otherwise leave blank)
            </legend>
            {showFees && (
              <>
                <p className="fu-hint">Security Deposit is $100 (required to hold the date). Remaining fees are due 2 weeks prior.</p>
                {form.spaces.map((s, i) => (
                  <div className="fu-row" key={i}>
                    <label>Space {i === 0 ? '(Church)' : i + 1}
                      <input
                        type="text"
                        value={s.name}
                        onChange={updateSpace(i, 'name')}
                        readOnly={i === 0}
                      />
                    </label>
                    <label>$
                      <input type="text" value={s.fee} onChange={updateSpace(i, 'fee')} placeholder="0.00" />
                    </label>
                  </div>
                ))}
                <div className="fu-row">
                  <label>Security/Open $
                    <input type="text" value={form.security_open_fee || ''} onChange={update('security_open_fee')} />
                  </label>
                  <label>Security/Close $
                    <input type="text" value={form.security_close_fee || ''} onChange={update('security_close_fee')} />
                  </label>
                </div>
                <label>Total Facility Fees $
                  <input type="text" value={form.total_fee || ''} onChange={update('total_fee')} />
                </label>
                <p className="fu-hint">Other line items (optional):</p>
                {form.extras.map((ex, i) => (
                  <div className="fu-row" key={i}>
                    <label>Label
                      <input type="text" value={ex.label} onChange={updateExtra(i, 'label')} />
                    </label>
                    <label>$
                      <input type="text" value={ex.amount} onChange={updateExtra(i, 'amount')} />
                    </label>
                  </div>
                ))}
              </>
            )}
          </fieldset>

          <fieldset>
            <legend>Signature</legend>
            <p className="fu-hint">
              By submitting, I certify that I am an authorized representative of this group; that the foregoing statements
              are true to the best of my knowledge; and that we will adhere to the Facility Rules and this Facility Use
              Agreement.
            </p>
            <p className="fu-hint">
              <strong>Indemnification:</strong> The named group agrees to indemnify and hold harmless St. Luke's ~ San Lucas
              Episcopal Church, its employees and agents from all claims, demands, actions, or other liability, injury or
              harm caused by any action or omission, arising out of the use of this premises for the duration of this
              event. If additional cleaning is required, the cost will be deducted from the Cleaning Deposit reimbursement.
            </p>
            <div className="fu-row">
              <label>Signature (printed name) <span className="fu-req">*</span>
                <input type="text" required value={form.signature_name || ''} onChange={update('signature_name')} />
              </label>
              <label>Date <span className="fu-req">*</span>
                <input type="date" required value={form.signature_date || ''} onChange={update('signature_date')} />
              </label>
            </div>
          </fieldset>

          <button type="submit" className="fu-btn fu-btn-primary" disabled={submitting}>
            {submitting ? 'Generating PDF…' : 'Generate filled PDF'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FacilityUsePage;
